/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { HomeIcon, ExitIcon, MixIcon, ResetIcon, Cross1Icon } from '@radix-ui/react-icons';
import { ReactComponent as MobileNewChatIcon } from '../Icon/MobileNewChatIcon.svg';
import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, authSelector } from '../../features/auth/authSlice';
import { useTheme } from '../Theme/ThemeProvider';
import { FeedbackDialog } from './FeedbackDialog';
import { ThemeDialog } from '../Dialogs/ThemeDialog';
import { RootState } from '../../store/store';
import { setChatMode, setRagChatType, clearRagHistory, clearaskChatHistory } from '../../store/chatModeSlice';
import { clearTranslationData, clearUploadedFiles } from '../../store/chatSlice';
import { getSavedBubbleColor, getBubbleStyle } from '../../utils/user/bubbleColors';
import { getVisibleRagOptions } from '../../utils/rag/ragConfig';
import { SearchInput } from '../common/SearchInput';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage?: 'main' | 'chat';
  onClearChat?: () => void;
  isMobile?: boolean;
  onShowWhatsNew?: () => void
}

export function MobileMenu({ isOpen, onClose, currentPage = 'main', onClearChat, isMobile = false, onShowWhatsNew }: MobileMenuProps) {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const auth = useSelector(authSelector);
  const { accent, theme } = useTheme();
  const isDark = theme.startsWith('dark');
  const chatModeState = useSelector((state: RootState) => state.chatMode);
  const [showFeedback, setShowFeedback] = useState(false);
  const [ragSearch, setRagSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'general' | '3m-knowledge'>(chatModeState.mode === '3M Knowledge' ? '3m-knowledge' : 'general');

  // Keep local tab in sync with Redux mode whenever the menu opens or mode changes externally
  useEffect(() => {
    setActiveTab(chatModeState.mode === '3M Knowledge' ? '3m-knowledge' : 'general');
    setRagSearch(''); // reset search when tab or open state changes
  }, [chatModeState.mode, isOpen]);

  // Reactively track the user bubble color (updated via ThemeDialog)
  const [userBubbleColor, setUserBubbleColor] = useState(() => getSavedBubbleColor());
  useEffect(() => {
    const sync = () => setUserBubbleColor(getSavedBubbleColor());
    window.addEventListener('user-bubble-color-change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('user-bubble-color-change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Only render for mobile devices
  if (!isOpen) return null;

  const getGradientColor = (accentColor: string) => {
    if (accentColor === 'purple') return '139, 92, 246';
    if (accentColor === 'blue') return '59, 130, 246';
    if (accentColor === 'green') return '16, 185, 129';
    if (accentColor === 'gold') return '245, 158, 11';
    return '209, 4, 17';
  };

  const gradientColor = getGradientColor(accent);

  const tabGlassBg = isDark
    ? 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 100%)'
    : 'linear-gradient(135deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.03) 100%)';
  const tabGlassBorder = isDark ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(0,0,0,0.14)';
  const tabGlassShadow = isDark
    ? '0 2px 10px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)'
    : '0 2px 10px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.7)';

  // Tab pills — glassy look matching the reference (neutral, no accent border)
  const tabGlassBgHover = isDark
    ? 'radial-gradient(140% 130% at 28% 22%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 52%, rgba(255,255,255,0.03) 100%)'
    : 'radial-gradient(140% 130% at 28% 22%, rgba(255,255,255,0.80) 0%, rgba(255,255,255,0.45) 55%, rgba(255,255,255,0.12) 100%)';

  const tabGlassBgActive = isDark
    ? 'radial-gradient(140% 130% at 28% 22%, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.16) 46%, rgba(255,255,255,0.07) 76%, rgba(255,255,255,0.03) 100%), linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)'
    : 'radial-gradient(140% 130% at 28% 22%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.60) 48%, rgba(255,255,255,0.26) 78%, rgba(255,255,255,0.12) 100%), linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.02) 100%)';

  const tabGlassBorderHover = isDark ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(0,0,0,0.16)';
  const tabGlassBorderActive = isDark ? '1px solid rgba(255,255,255,0.46)' : '1px solid rgba(0,0,0,0.18)';

  const tabGlassShadowHover = isDark
    ? '0 6px 16px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)'
    : '0 6px 16px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.75)';

  const tabGlassShadowActive = isDark
    ? '0 10px 24px rgba(0,0,0,0.62), inset 0 1px 0 rgba(255,255,255,0.26), inset 0 -1px 0 rgba(0,0,0,0.32), 0 0 0 1px rgba(255,255,255,0.10)'
    : '0 10px 22px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(0,0,0,0.06)';

  // RAG list button styling — glassy look (active matches reference: neutral/grey, no accent ring)
  const ragGlassBg = isDark
    ? 'radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)'
    : 'radial-gradient(circle at center, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.03) 55%, rgba(0,0,0,0.01) 100%)';

  const ragGlassBgHover = isDark
    ? 'radial-gradient(circle at center, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 100%)'
    : 'radial-gradient(circle at center, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0.04) 55%, rgba(0,0,0,0.015) 100%)';

  // Selected item: brighter neutral highlight (no accent tint)
  const ragGlassBgActive = isDark
    ? 'radial-gradient(140% 130% at 28% 22%, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.16) 46%, rgba(255,255,255,0.07) 76%, rgba(255,255,255,0.03) 100%), linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)'
    : 'radial-gradient(140% 130% at 28% 22%, rgba(255,255,255,0.90) 0%, rgba(255,255,255,0.55) 48%, rgba(255,255,255,0.25) 78%, rgba(255,255,255,0.12) 100%), linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.02) 100%)';

  const ragGlassBorder = isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.14)';
  const ragGlassBorderHover = isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.20)';

  const ragGlassShadow = isDark
    ? '0 1px 2px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.08)'
    : '0 1px 2px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.65)';

  const ragGlassShadowHover = isDark
    ? '0 8px 20px rgba(0,0,0,0.46), inset 0 1px 0 rgba(255,255,255,0.10), 0 0 0 1px rgba(255,255,255,0.04)'
    : '0 8px 18px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.75)';

  // Active: subtle depth + inner highlight (no outer ring)
  const ragGlassShadowActive = isDark
    ? '0 10px 24px rgba(0,0,0,0.60), inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -1px 0 rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)'
    : 'inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.08)';

  // Active item text/icon color should remain readable in light mode
  const activeRagItemColor = isDark ? '#ffffff' : 'var(--color-text)';
  const inactiveRagItemColor = 'var(--color-text)';

  // Get user initials from MSAL account
  const account = instance.getActiveAccount();
  const userName = account?.name || account?.username || '';
  const initials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n: string) => n[0].toUpperCase())
    .join('');

  const handleLogout = () => {
    if (isMobile) {
      instance.logoutRedirect();
    } else {
      instance.logoutPopup();
    }
    dispatch(logout());
    onClose();
  };

  const handleNavigateHome = () => {
    if (chatModeState.isStreaming) return;
    dispatch(setChatMode('Ask Chat'));
    dispatch(setRagChatType(null));
    dispatch(clearaskChatHistory());
    dispatch(clearRagHistory());
    dispatch(clearUploadedFiles());
    dispatch(clearTranslationData());
    if (onClearChat) onClearChat();
    onClose();
  };

  const handleRagSelect = (chatType: NonNullable<import('../../store/chatModeSlice').RagChatType>) => {
    // Only block re-clicking the same RAG while streaming (would clear the active conversation).
    // Switching to a different RAG is safe — each REST RAG has its own independent hook instance.
    if (chatModeState.isStreaming && chatModeState.ragChatType === chatType) return;
    dispatch(clearRagHistory());
    dispatch(clearTranslationData());
    dispatch(clearUploadedFiles());
    dispatch(setChatMode('3M Knowledge'));
    dispatch(setRagChatType(chatType));
    onClose();
  };

  const handleNewRagChat = () => {
    if (chatModeState.isStreaming) return;
    dispatch(clearRagHistory());
    dispatch(clearTranslationData());
    dispatch(clearUploadedFiles());
    if (onClearChat) onClearChat();
    onClose();
  };

  const handleSelect3MKnowledge = () => {
    if (chatModeState.isStreaming) return;
    setActiveTab('3m-knowledge');
    dispatch(setChatMode('3M Knowledge'));
    if (!chatModeState.ragChatType) {
      dispatch(clearRagHistory());
      dispatch(clearTranslationData());
      dispatch(clearUploadedFiles());
      dispatch(setRagChatType('Everyday Insights'));
    }
  };

  // RAG items for the 3M Knowledge tab — derived from central ragConfig registry
  const ragItems = getVisibleRagOptions().map(cfg => ({
    key: cfg.key as typeof cfg.key,
    label: cfg.label,
    icon: (_isActive: boolean) => React.createElement(cfg.iconComponent, { ...cfg.iconProps, width: 18, height: 18, size: 18 }),
  }));

  const filteredRagItems = ragSearch
    ? ragItems.filter(item => item.label.toLowerCase().includes(ragSearch.toLowerCase()))
    : ragItems;


  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[20100] transition-opacity"
        onClick={onClose}
      />

      {/* Full-screen menu overlay */}
      <div
        className="fixed inset-0 z-[20200] flex flex-col"
        style={{ backgroundColor: 'var(--color-bg)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-color)]" style={{ backgroundColor: 'var(--color-header-bg)' }}>
          {/* Close button */}
          <button
            aria-label="Close Menu"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 focus:outline-none flex-shrink-0"
            style={{ color: '#ffffff' }}
          >
            <Cross1Icon width={20} height={20} />
          </button>

          {/* Centered branding */}
          <div className="flex items-center gap-2">
            <img src="/logo-dark.png" alt="3M Navigator" className="h-10 w-auto" />
          </div>

          {/* New chat button — only shown on chat page when a RAG is active */}
          {currentPage === 'chat' && activeTab === '3m-knowledge' && chatModeState.ragChatType !== null && onClearChat ? (
          <button
            aria-label="New Chat"
            onClick={() => { handleNewRagChat(); }}
            disabled={chatModeState.isStreaming}
            className={`relative flex items-center justify-center focus:outline-none flex-shrink-0 ${
              chatModeState.isStreaming ? 'opacity-40 cursor-not-allowed' : ''
            }`}
            style={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              background: 'radial-gradient(circle at center, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 100%)',
              border: '1px solid rgba(255,255,255,0.28)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)',
              color: '#ffffff',
            }}
          >
            {/* Glass border overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.20) 20%, rgba(255,255,255,0) 50%, rgba(255,255,255,0.20) 80%, rgba(255,255,255,0.55) 100%)',
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'xor',
                WebkitMaskComposite: 'xor',
                padding: '1px',
              }}
            />
            <MobileNewChatIcon width={18} height={18} style={{ color: '#ffffff', position: 'relative', zIndex: 1 }} />
          </button>
          ) : (
            <div style={{ width: 36, height: 36, flexShrink: 0 }} />
          )}
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            disabled={chatModeState.isStreaming}
            onClick={() => {
              setActiveTab('general');
              if (chatModeState.mode !== 'Ask Chat') {
                dispatch(setChatMode('Ask Chat'));
                dispatch(setRagChatType(null));
              }
            }}
            className={`mm-tab-btn relative flex-1 px-3 py-2 text-sm transition-all ${chatModeState.isStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={
              activeTab === 'general'
                ? {
                    background: tabGlassBgActive,
                    color: isDark ? '#ffffff' : 'var(--color-text)',
                    border: tabGlassBorderActive,
                    borderRadius: '12px',
                    boxShadow: tabGlassShadowActive,
                    fontSize: '14px',
                    fontWeight: 500,
                    backdropFilter: 'blur(18px) saturate(200%)',
                    WebkitBackdropFilter: 'blur(18px) saturate(200%)',
                  }
                : {
                    background: tabGlassBg,
                    color: 'var(--color-text)',
                    border: tabGlassBorder,
                    opacity: 0.78,
                    borderRadius: '12px',
                    boxShadow: tabGlassShadow,
                    fontWeight: 500,
                    fontSize: '14px',
                    backdropFilter: 'blur(14px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(14px) saturate(180%)',
                  }
            }
            onMouseEnter={(e) => {
              if (chatModeState.isStreaming) return;
              if (activeTab === 'general') return;
              const el = e.currentTarget as HTMLElement;
              el.style.background = tabGlassBgHover;
              el.style.border = tabGlassBorderHover;
              el.style.boxShadow = tabGlassShadowHover;
            }}
            onMouseLeave={(e) => {
              if (chatModeState.isStreaming) return;
              if (activeTab === 'general') return;
              const el = e.currentTarget as HTMLElement;
              el.style.background = tabGlassBg;
              el.style.border = tabGlassBorder;
              el.style.boxShadow = tabGlassShadow;
            }}
          >
            {/* subtle glass border highlight */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                borderRadius: '12px',
                background: activeTab === 'general'
                  ? (isDark
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.34) 28%, rgba(255,255,255,0.10) 60%, rgba(255,255,255,0.55) 100%)'
                      : 'linear-gradient(135deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.10) 35%, rgba(0,0,0,0.04) 60%, rgba(0,0,0,0.16) 100%)')
                  : 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.22) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.22) 80%, rgba(255,255,255,0.55) 100%)',
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'xor',
                WebkitMaskComposite: 'xor',
                padding: activeTab === 'general' ? '2px' : '1.5px',
              }}
            />
            <span style={{ position: 'relative', zIndex: 1 }}>General</span>
          </button>

          <button
            disabled={chatModeState.isStreaming}
            onClick={handleSelect3MKnowledge}
            className={`mm-tab-btn relative flex-1 px-3 py-2 text-sm transition-all ${chatModeState.isStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={
              activeTab === '3m-knowledge'
                ? {
                    background: tabGlassBgActive,
                    color: isDark ? '#ffffff' : 'var(--color-text)',
                    border: tabGlassBorderActive,
                    borderRadius: '12px',
                    boxShadow: tabGlassShadowActive,
                    fontSize: '14px',
                    fontWeight: 500,
                    backdropFilter: 'blur(18px) saturate(210%)',
                    WebkitBackdropFilter: 'blur(18px) saturate(210%)',
                  }
                : {
                    background: tabGlassBg,
                    color: 'var(--color-text)',
                    border: tabGlassBorder,
                    opacity: 0.78,
                    borderRadius: '12px',
                    boxShadow: tabGlassShadow,
                    fontWeight: 500,
                    fontSize: '14px',
                    backdropFilter: 'blur(14px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(14px) saturate(180%)',
                  }
            }
            onMouseEnter={(e) => {
              if (chatModeState.isStreaming) return;
              if (activeTab === '3m-knowledge') return;
              const el = e.currentTarget as HTMLElement;
              el.style.background = tabGlassBgHover;
              el.style.border = tabGlassBorderHover;
              el.style.boxShadow = tabGlassShadowHover;
            }}
            onMouseLeave={(e) => {
              if (chatModeState.isStreaming) return;
              if (activeTab === '3m-knowledge') return;
              const el = e.currentTarget as HTMLElement;
              el.style.background = tabGlassBg;
              el.style.border = tabGlassBorder;
              el.style.boxShadow = tabGlassShadow;
            }}
          >
            {/* subtle glass border highlight */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                borderRadius: '12px',
                background: activeTab === '3m-knowledge'
                  ? (isDark
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.34) 28%, rgba(255,255,255,0.10) 60%, rgba(255,255,255,0.55) 100%)'
                      : 'linear-gradient(135deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.10) 35%, rgba(0,0,0,0.04) 60%, rgba(0,0,0,0.16) 100%)')
                  : 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.22) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.22) 80%, rgba(255,255,255,0.55) 100%)',
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'xor',
                WebkitMaskComposite: 'xor',
                padding: activeTab === '3m-knowledge' ? '2px' : '1.5px',
              }}
            />
            <span style={{ position: 'relative', zIndex: 1 }}>3M Knowledge</span>
          </button>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-3 pb-4">
          <div style={{ marginBottom: '10px', borderColor: '#424248', borderTopWidth: '1px', borderStyle: 'solid', opacity: 0.6 }} />
          {/* ── General Tab ── */}
          {activeTab === 'general' && (
            <nav className="space-y-1 mt-1">
              <button
                onClick={handleNavigateHome}
                className="mm-general-btn w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left"
                style={{
                  backgroundColor: currentPage === 'main'
                    ? 'var(--color-accent)'
                    : 'transparent',
                  color: currentPage === 'main'
                    ? '#ffffff'
                    : 'var(--color-text)',
                }}
              >
                <HomeIcon width={20} height={20} />
                <span className="text-sm font-medium translate-y-[2px]">Home</span>
                {currentPage === 'main' && (
                  <span
                    className="ml-auto w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}
                  />
                )}
              </button>
              <div className="my-2 mx-2 border-t border-[var(--border-color)] opacity-40" />
            
              {/* Theme Settings */}
              <div className="relative" data-tutorial="theme-button">
                <button
                  className="mm-general-btn w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left group"
                  style={{ color: 'var(--color-text)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                >
                  <MixIcon width={20} height={20} className="flex-shrink-0 transition-all group-hover:font-bold" />
                  <span className="text-sm font-medium translate-y-[2px]">Theme Settings</span>
                </button>
                <div className="absolute inset-0 bg-transparent">
                  <ThemeDialog />
                </div>
              </div>

              {/* What's New */}
              {onShowWhatsNew && (
                <button
                  onClick={() => { onShowWhatsNew(); onClose(); }}
                  className="mm-general-btn w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left"
                  style={{ color: 'var(--color-text)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                >
                  <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.49991 0.876892C3.84222 0.876892 0.877075 3.84204 0.877075 7.49972C0.877075 11.1574 3.84222 14.1226 7.49991 14.1226C11.1576 14.1226 14.1227 11.1574 14.1227 7.49972C14.1227 3.84204 11.1576 0.876892 7.49991 0.876892ZM1.82707 7.49972C1.82707 4.36671 4.36689 1.82689 7.49991 1.82689C10.6329 1.82689 13.1727 4.36671 13.1727 7.49972C13.1727 10.6327 10.6329 13.1726 7.49991 13.1726C4.36689 13.1726 1.82707 10.6327 1.82707 7.49972ZM8.24992 4.49999C8.24992 4.9142 7.91413 5.24999 7.49992 5.24999C7.08571 5.24999 6.74992 4.9142 6.74992 4.49999C6.74992 4.08577 7.08571 3.74999 7.49992 3.74999C7.91413 3.74999 8.24992 4.08577 8.24992 4.49999ZM6.00003 5.99999H6.50003H7.50003C7.77618 5.99999 8.00003 6.22384 8.00003 6.49999V9.99999H8.50003H9.00003V11H8.50003H7.50003H6.50003H6.00003V9.99999H6.50003H7.00003V6.99999H6.50003H6.00003V5.99999Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                  </svg>
                  <span className="text-sm font-medium translate-y-[2px]">What's New</span>
                </button>
              )}

              {/* Feedback */}
              <button
                onClick={() => setShowFeedback(true)}
                className="mm-general-btn w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left"
                style={{ color: 'var(--color-text)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                <ResetIcon width={20} height={20} />
                <span className="text-sm font-medium translate-y-[2px]">Feedback</span>
              </button>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="mm-general-btn w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left"
                style={{ color: 'var(--color-text)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                <ExitIcon width={20} height={20} />
                <span className="text-sm font-medium translate-y-[2px]">Logout</span>
              </button>
            </nav>
          )}

          {/* ── 3M Knowledge Tab ── */}
          {activeTab === '3m-knowledge' && (
            <nav className="mm-rag-nav flex flex-col items-center gap-1 mt-1 px-2">
              <SearchInput
                value={ragSearch}
                onChange={setRagSearch}
                placeholder="Search assistants..."
                containerClassName="w-full mb-2"
                inputClassName="placeholder-style top-heavy-input"
                iconSize={15}
              />
              {filteredRagItems.length === 0 && (
                <p className="text-sm text-center py-4" style={{ color: 'var(--color-text-secondary, #6b7280)', fontFamily: '3MCircularTT' }}>
                  No assistants match your search.
                </p>
              )}
              {filteredRagItems.map((item) => {
                const isActive = chatModeState.ragChatType === item.key;
                return (
                  <button
                    key={item.key}
                    disabled={chatModeState.isStreaming && chatModeState.ragChatType === item.key}
                    onClick={() => handleRagSelect(item.key)}
                    className={`mm-rag-btn relative flex items-center gap-3 mb-1 px-3 py-2 transition-all text-left ${chatModeState.isStreaming && chatModeState.ragChatType === item.key ? 'opacity-50 cursor-not-allowed' : ''} ${isActive && !isDark ? 'mm-rag-active-light' : ''}`}
                    style={{
                      width: '80%',
                      maxWidth: '420px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      background: isActive ? ragGlassBgActive : ragGlassBg,
                      animation: 'slideInFromLeft 0.18s ease-out',
                      color: isActive ? activeRagItemColor : inactiveRagItemColor,
                      backdropFilter: 'blur(14px) saturate(190%)',
                      WebkitBackdropFilter: 'blur(14px) saturate(190%)',
                      boxShadow: isActive ? ragGlassShadowActive : ragGlassShadow,
                      border: isActive
                        ? (isDark ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(94,149,237,0.55)')
                        : ragGlassBorder,
                      transition: 'transform 0.8s cubic-bezier(0.11, 0, 0.3, 0), all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      if (!isActive) {
                        el.style.background = ragGlassBgHover;
                        el.style.border = ragGlassBorderHover;
                        el.style.boxShadow = ragGlassShadowHover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      if (!isActive) {
                        el.style.background = ragGlassBg;
                        el.style.border = ragGlassBorder;
                        el.style.boxShadow = ragGlassShadow;
                      }
                    }}
                  >
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        borderRadius: '12px',
                        background: isDark
                          ? (isActive
                            ? 'linear-gradient(135deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.34) 28%, rgba(255,255,255,0.10) 60%, rgba(255,255,255,0.55) 100%)'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.25) 80%, rgba(255,255,255,0.6) 100%)')
                          : (isActive
                            ? 'linear-gradient(135deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.10) 28%, rgba(0,0,0,0.03) 60%, rgba(0,0,0,0.14) 100%)'
                            : 'linear-gradient(135deg, rgba(0,0,0,0.14) 0%, rgba(0,0,0,0.06) 20%, rgba(0,0,0,0.01) 40%, rgba(0,0,0,0.01) 60%, rgba(0,0,0,0.06) 80%, rgba(0,0,0,0.14) 100%)'),
                        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        maskComposite: 'xor',
                        WebkitMaskComposite: 'xor',
                        padding: isActive ? '2px' : '1.5px',
                      }}
                    />
                    <span className="flex-shrink-0" style={{ position: 'relative', zIndex: 1 }}>
                      {item.icon(isActive)}
                    </span>
                    <span className="mm-rag-label text-sm font-medium top-heavy" style={{ position: 'relative', zIndex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          )}


        </div>

        {/* User info footer */}
        {userName && (
          <div className="px-4 py-3 border-t border-[var(--border-color)] flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-sm font-bold"
              style={auth.photo ? undefined : getBubbleStyle(userBubbleColor, isDark ? 'dark' : 'light')}
            >
              {auth.photo ? (
                <img
                  src={auth.photo}
                  alt={userName}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                initials || '?'
              )}
            </div>
            <span className="text-sm font-medium truncate" style={{ color: 'var(--color-text)' }}>
              {userName}
            </span>
          </div>
        )}

        {/* Gradient bottom border */}
        <div
          className="w-full h-1"
          style={{
            background: `radial-gradient(ellipse at center, rgb(${gradientColor}) 0%, rgb(${gradientColor}) 40%, rgba(${gradientColor}, 0.3) 60%, rgba(${gradientColor}, 0) 80%)`,
          }}
        />
      </div>

      {/* Feedback Dialog */}
      <FeedbackDialog
        showFeedback={showFeedback}
        setShowFeedback={setShowFeedback}
        fullScreen={isMobile}
      />
    </>
  );
}
